import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import clsx from 'clsx';
import BrandLoader from '../shared/brand/BrandLoader';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../shared/feed/feedTime';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PiXLight,
  PiCheckCircleLight,
  PiWarningLight,
  PiQuestionLight,
  PiFileTextLight,
  PiColumnsLight,
  PiQuotesLight,
  PiTargetLight,
  PiShieldCheckLight,
  PiSpinnerGapLight,
  PiChatTextLight,
  PiPaperPlaneRightLight,
  PiPencilSimpleLight,
  PiTrashLight,
  PiAtLight,
  PiSparkleLight,
  PiCaretDownLight,
} from 'react-icons/pi';
import { TableCell, TableColumn, TableRow, TableSharedUser, CellComment } from '../../types/batchTables';
import {
  fetchCellComments,
  addCellComment,
  updateCellComment,
  deleteCellComment,
  createDeepDive,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { ComplianceStatus, RiskLevel } from '../../types/batchTables';
import Button from '../shared/TailwindComponents/Button';
import GlassCard from '../shared/brand/GlassCard';
import ProfileImage from '../Header/ProfileImage';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, cellDetailTourSteps } from '../shared/FeatureTour';
import { normalise } from '../shared/access/accessModel';
import Spinner from '../shared/TailwindComponents/Spinner';

interface CellDetailPanelProps {
  cell: TableCell | null;
  column: TableColumn;
  row: TableRow;
  tableUuid: string;
  onClose: () => void;
  /**
   * Rendered inside a docked pane rather than as a fixed overlay.
   *
   * The overlay covered the grid and made inspecting a cell exclusive with
   * reading the table. Docked, it sits BESIDE the grid - which is the whole
   * point, since a cell only means something next to its row. The pane
   * supplies the glass, the header and the close control, so this drops all
   * three.
   */
  docked?: boolean;
}

const confidenceConfig: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  high: {
    label: 'High',
    color: 'text-success-700 dark:text-success-400',
    bg: 'bg-success-50 dark:bg-success-900/20',
    border: 'border-success-200 dark:border-success-800',
    icon: <PiCheckCircleLight className="w-4 h-4 text-success-500" />,
  },
  medium: {
    label: 'Medium',
    color: 'text-warning-700 dark:text-warning-400',
    bg: 'bg-warning-50 dark:bg-warning-900/20',
    border: 'border-warning-200 dark:border-warning-800',
    icon: <PiShieldCheckLight className="w-4 h-4 text-warning-500" />,
  },
  low: {
    label: 'Low',
    color: 'text-danger-700 dark:text-danger-400',
    bg: 'bg-danger-50 dark:bg-danger-900/20',
    border: 'border-danger-200 dark:border-danger-800',
    icon: <PiWarningLight className="w-4 h-4 text-danger-500" />,
  },
  not_found: {
    label: 'Not found',
    color: 'text-gray-500 dark:text-gray-400',
    bg: 'bg-surface-sunken',
    border: 'border-hairline',
    icon: <PiQuestionLight className="w-4 h-4 text-gray-400" />,
  },
};

/**
 * The heading that separates one part of the panel from the next.
 *
 * The panel was a single undifferentiated scroll: what the cell IS, what the AI
 * concluded, how to go deeper and what people said about it, all in one column
 * with nothing to say where one ended. Three quiet labels do that without
 * costing a tab bar's worth of state or hiding anything behind a click.
 */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="pt-1 text-[11px] font-medium text-fg-muted">
    {children}
  </p>
);

const complianceStatusConfig: Record<ComplianceStatus, { label: string; bg: string; text: string; dot: string }> = {
  compliant: {
    label: 'Compliant',
    bg: 'bg-success-50 dark:bg-success-900/20',
    text: 'text-success-700 dark:text-success-400',
    dot: 'bg-success-500',
  },
  non_compliant: {
    label: 'Non-Compliant',
    bg: 'bg-danger-50 dark:bg-danger-900/20',
    text: 'text-danger-700 dark:text-danger-400',
    dot: 'bg-danger-500',
  },
  needs_review: {
    label: 'Review',
    bg: 'bg-warning-50 dark:bg-warning-900/20',
    text: 'text-warning-700 dark:text-warning-400',
    dot: 'bg-warning-500',
  },
  not_found: {
    label: 'Not Found',
    bg: 'bg-surface-sunken',
    text: 'text-gray-500 dark:text-gray-400',
    dot: 'bg-gray-400',
  },
};

const riskLevelConfig: Record<RiskLevel, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'text-danger-600 dark:text-danger-400' },
  major: { label: 'Major', color: 'text-warning-600 dark:text-warning-400' },
  minor: { label: 'Minor', color: 'text-warning-600 dark:text-warning-400' },
  info: { label: 'Info', color: 'text-info-600 dark:text-info-400' },
};

const CellDetailPanel: React.FC<CellDetailPanelProps> = ({
  cell, column, row, tableUuid, onClose, docked,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const conf = cell?.confidence ? confidenceConfig[cell.confidence] || confidenceConfig.not_found : null;

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'cell-detail',
    steps: cellDetailTourSteps,
    readyToShow: !!cell,
    autoStartDelay: 600,
  });

  // User identity for ownership checks
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const currentUserUuid = userInfo?.UserUID || userInfo?.uuid_encrypted || '';

  // Collaborators for @mention - use has_access (owner + collaborators merged) with shared_with fallback
  const currentTable = useSelector((state: RootState) => state.batchTables.currentTable);
  const currentUserEmail = (userInfo?.Email || '').toLowerCase();
  const mentionableUsers = useMemo<TableSharedUser[]>(() => {
    // Prefer has_access (merged owner + collaborators), fall back to shared_with + owner
    let allUsers: TableSharedUser[] = [];
    if (currentTable?.has_access && currentTable.has_access.length > 0) {
      allUsers = currentTable.has_access;
    } else {
      if (currentTable?.owner) {
        allUsers.push({
          user_id: currentTable.owner.user_id,
          name: currentTable.owner.name,
          email: currentTable.owner.email,
          initials: currentTable.owner.initials,
        });
      }
      if (currentTable?.shared_with) {
        const ownerIdStr = currentTable.owner ? String(currentTable.owner.user_id) : null;
        for (const u of currentTable.shared_with) {
          if (ownerIdStr && String(u.user_id) === ownerIdStr) continue;
          allUsers.push(u);
        }
      }
    }
    // Filter out self by email (most reliable cross-identifier match)
    return allUsers.filter(
      (u) => u.email.toLowerCase() !== currentUserEmail
    );
  }, [currentTable, currentUserEmail]);

  // Cell comments state from Redux
  const cellComments = useSelector((state: RootState) => state.batchTables.cellComments);
  const isLoadingComments = useSelector((state: RootState) => state.batchTables.isLoadingComments);

  // Local state for comment input
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const commentsScrollRef = useRef<HTMLDivElement>(null);

  // @mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionTarget, setMentionTarget] = useState<'new' | 'edit'>('new');
  const mentionDropdownRef = useRef<HTMLDivElement>(null);

  const filteredMentionUsers = useMemo(() => {
    if (!mentionSearch) return mentionableUsers;
    const q = mentionSearch.toLowerCase();
    return mentionableUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [mentionableUsers, mentionSearch]);

  // Load comments when cell changes
  useEffect(() => {
    if (cell?.id && tableUuid) {
      dispatch(fetchCellComments(tableUuid, cell.id) as any);
    }
  }, [dispatch, tableUuid, cell?.id]);

  /*
    THE PANEL BODY SCROLLS ITSELF. Nothing else moves.

    This was `commentsEndRef.scrollIntoView()`, and that does not scroll one
    box: it walks EVERY scrollable ancestor and moves each until the node is in
    view. The comments sit inside this panel, inside the table detail, inside
    the page - so adding a comment dragged the whole page along with them. It
    also fired on mount with no comments at all.

    Setting `scrollTop` moves exactly one box and cannot reach an ancestor, and
    only when there is something to scroll. (Same fix as the data-source Test
    pane - see `DataSourceAssistantPane`.)
  */
  /*
    OPEN WHEN THERE IS SOMETHING TO READ - but only until the reader disagrees.

    The comments arrive after the panel does, so the state cannot simply be
    seeded from the count: it has to open when the fetch lands. What it must
    NOT do is re-open a section the reader just closed, which is what keying
    the effect on the count alone would do every time a comment was posted. So
    a manual toggle marks the cell as decided, and the auto-open stands down
    until a different cell is opened.
  */
  const commentsTouchedRef = useRef(false);
  useEffect(() => {
    commentsTouchedRef.current = false;
    setCommentsOpen(false);
  }, [cell?.id]);
  useEffect(() => {
    if (commentsTouchedRef.current) return;
    if (cellComments.length > 0) setCommentsOpen(true);
  }, [cellComments.length]);

  useEffect(() => {
    const box = commentsScrollRef.current;
    if (!box) return;
    if (box.scrollHeight <= box.clientHeight) return;
    box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [cellComments.length]);

  // Close mention dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(e.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    if (showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMentionDropdown]);

  // ─── Mention helpers ────────────────────────────────────────────────

  /** Find the @query being typed at cursor position */
  const getMentionQuery = (text: string, cursorPos: number): string | null => {
    const beforeCursor = text.slice(0, cursorPos);
    const match = beforeCursor.match(/@([^\s@\[\]]*)$/);
    return match ? match[1] : null;
  };

  /** Insert a mention at the current @ position */
  const insertMention = (user: TableSharedUser, target: 'new' | 'edit') => {
    const isNew = target === 'new';
    const text = isNew ? commentText : editingText;
    const ref = isNew ? commentInputRef.current : null;
    const cursorPos = ref?.selectionStart ?? text.length;

    const beforeCursor = text.slice(0, cursorPos);
    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;

    const mentionTag = `@[${user.name}] `;
    const newText = text.slice(0, atIndex) + mentionTag + text.slice(cursorPos);

    if (isNew) {
      setCommentText(newText);
      // Move cursor after the mention
      setTimeout(() => {
        if (commentInputRef.current) {
          const newPos = atIndex + mentionTag.length;
          commentInputRef.current.selectionStart = newPos;
          commentInputRef.current.selectionEnd = newPos;
          commentInputRef.current.focus();
        }
      }, 0);
    } else {
      setEditingText(newText);
    }

    setShowMentionDropdown(false);
    setMentionSearch('');
    setMentionIndex(0);
  };

  /** Handle textarea change with @ detection */
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setCommentText(val);

    const cursorPos = e.target.selectionStart;
    const query = getMentionQuery(val, cursorPos);

    if (query !== null && mentionableUsers.length > 0) {
      setMentionSearch(query);
      setMentionTarget('new');
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  /** Handle edit textarea change with @ detection */
  const handleEditCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setEditingText(val);

    const cursorPos = e.target.selectionStart;
    const query = getMentionQuery(val, cursorPos);

    if (query !== null && mentionableUsers.length > 0) {
      setMentionSearch(query);
      setMentionTarget('edit');
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  /** Trigger @ menu manually via button */
  const handleAtButtonClick = () => {
    if (!commentInputRef.current) return;
    const ref = commentInputRef.current;
    const cursorPos = ref.selectionStart;
    const newText = commentText.slice(0, cursorPos) + '@' + commentText.slice(cursorPos);
    setCommentText(newText);
    setMentionSearch('');
    setMentionTarget('new');
    setShowMentionDropdown(true);
    setMentionIndex(0);
    setTimeout(() => {
      ref.selectionStart = cursorPos + 1;
      ref.selectionEnd = cursorPos + 1;
      ref.focus();
    }, 0);
  };

  // ─── Comment CRUD ───────────────────────────────────────────────────

  const handleAddComment = useCallback(async () => {
    if (!commentText.trim() || !cell?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dispatch(addCellComment(tableUuid, cell.id, commentText.trim()) as any);
      setCommentText('');
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, tableUuid, cell?.id, commentText, isSubmitting]);

  const handleUpdateComment = useCallback(async (commentId: number) => {
    if (!editingText.trim() || !cell?.id) return;
    await dispatch(updateCellComment(tableUuid, cell.id, commentId, editingText.trim()) as any);
    setEditingCommentId(null);
    setEditingText('');
  }, [dispatch, tableUuid, cell?.id, editingText]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    if (!cell?.id) return;
    await dispatch(deleteCellComment(tableUuid, cell.id, commentId) as any);
  }, [dispatch, tableUuid, cell?.id]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention dropdown keyboard navigation
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionIndex], mentionTarget);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  }, [handleAddComment, showMentionDropdown, filteredMentionUsers, mentionIndex, mentionTarget]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>, commentId: number) => {
    // Mention dropdown keyboard navigation
    if (showMentionDropdown && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionUsers[mentionIndex], mentionTarget);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleUpdateComment(commentId);
    }
    if (e.key === 'Escape') {
      setEditingCommentId(null);
      setEditingText('');
    }
  }, [handleUpdateComment, showMentionDropdown, filteredMentionUsers, mentionIndex, mentionTarget]);

  const formatCommentTime = (dateStr: string | null) =>
    formatRelativeTime(dateStr || undefined, t);

  /** Render highlighted overlay for textareas - shows @mentions as blue text.
   *  The overlay has the EXACT same characters as the textarea value so they
   *  stay pixel-aligned. The @[ and ] around mentions are rendered transparent
   *  while the name itself is rendered in blue. Non-mention text uses the
   *  normal text color. The textarea text is fully transparent. */
  const renderInputHighlight = (text: string): React.ReactNode => {
    if (!text) return '\u00A0';
    const mentionRegex = /@\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Normal text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // @ and [ - keep as invisible characters for alignment
      parts.push(<span key={`pre-${match.index}`} className="text-transparent">@[</span>);
      // The name - visible in blue
      parts.push(
        <span key={`hl-${match.index}`} className="text-info-600 dark:text-info-400 font-medium">
          {match[1]}
        </span>
      );
      // ] - invisible for alignment
      parts.push(<span key={`post-${match.index}`} className="text-transparent">]</span>);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    parts.push('\u00A0');
    return parts;
  };

  /** Render comment text with @[Name] mentions highlighted */
  const renderCommentText = (text: string): React.ReactNode => {
    // Split on @[Name] pattern
    const mentionRegex = /@\[([^\]]+)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Text before the mention
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // The mention - clean inline blue text
      const mentionName = match[1];
      parts.push(
        <span
          key={`mention-${match.index}`}
          className="text-info-600 dark:text-info-400 font-medium"
        >
          @{mentionName}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    // Remaining text after last mention
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  /** Mention dropdown component (shared between new & edit) */
  const MentionDropdown = () => {
    if (!showMentionDropdown) return null;
    return (
      <div
        ref={mentionDropdownRef}
        className="glass absolute bottom-full start-0 end-0 mb-1 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
      >
        <div className="px-2.5 py-1.5 border-b border-hairline-soft">
          <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500">
            {t('Mention someone')}
          </span>
        </div>
        {filteredMentionUsers.length === 0 ? (
          <div className="px-3 py-3 text-center">
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              {mentionableUsers.length === 0
                ? t('No collaborators on this table yet')
                : t('No matching collaborators')
              }
            </p>
          </div>
        ) : (
          filteredMentionUsers.map((user, idx) => (
            <button
              key={String(user.user_id)}
              type="button"
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-start transition-colors ${
                idx === mentionIndex
                  ? 'bg-info-50 dark:bg-info-900/20'
                  : 'hover:bg-surface-sunken/70'
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent textarea blur
                insertMention(user, mentionTarget);
              }}
              onMouseEnter={() => setMentionIndex(idx)}
            >
              <ProfileImage
                profilePicture={normalise(user).avatar}
                email={user.email || null}
                initials={user.initials || user.name?.charAt(0) || '?'}
                name={user.name}
                size="w-6 h-6"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{user.name}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{user.email}</p>
              </div>
            </button>
          ))
        )}
      </div>
    );
  };

  const body = (
    <>
        {/* Header - the docked pane draws its own, so this is the overlay's. */}
        {!docked && (
        <div className="flex items-center justify-between px-5 py-3 border-b border-hairline flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-sunken flex items-center justify-center">
              <PiTargetLight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('Cell detail')}
              </h3>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-none mt-0.5">
                {column.name}
              </p>
            </div>
          </div>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
        )}

        {/* Content - plain inside a pane; the gradient was overlay chrome.
            This box is the panel's ONE scroller; the comments effect above
            moves its `scrollTop` rather than calling `scrollIntoView`, which
            would have taken the page with it. */}
        <div ref={commentsScrollRef} className={clsx(
          'flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-4',
          !docked && 'bg-gradient-to-b from-slate-50/80 via-white/50 to-info-50/40 dark:from-gray-950/80 dark:via-gray-900/60 dark:to-gray-950/80'
        )}>
          {/*
            ONE RHYTHM. Every block below is a `GlassCard` - the app's card
            standard - so padding, radius, surface and the muted `text-xs`
            header are decided once instead of per block. Before this the cards
            were hand-rolled `p-3`/`p-4 rounded-xl glass` with their labels
            sometimes inside and sometimes floating above, and Answer + Source
            Excerpt sat in an unspaced wrapper div, which is why the column
            looked unevenly gapped: those two were flush while everything else
            had 16px.
          */}

          {/* ── RESULT ────────────────────────────────────────────────────── */}
          {/* The answer leads the pane - it is why the cell was opened. */}
          <SectionLabel>{t('Result')}</SectionLabel>

          {/*
            THE VERDICT IS THE ANSWER, on a compliance cell.

            These cells carry no free-text value - the status and the reasoning
            ARE the finding - so the panel used to draw an "Answer" card holding
            an em-dash under every one of them. An empty field reads as missing
            data; there is nothing missing. The verdict leads instead, and the
            Answer card below renders only when a cell actually has a value.
          */}
          {cell?.compliance_status && (() => {
            const csConfig = complianceStatusConfig[cell.compliance_status!];
            const rlConfig = cell.compliance_status === 'non_compliant' && cell.risk_level
              ? riskLevelConfig[cell.risk_level]
              : null;
            return (
              <GlassCard padding="sm" className="border-s-2 border-s-champagne">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${csConfig.bg} ${csConfig.text}`}>
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${csConfig.dot}`} />
                    {csConfig.label}
                  </span>
                  {rlConfig && (
                    <span className={`text-xs font-medium ${rlConfig.color}`}>
                      {rlConfig.label} {t('Risk')}
                    </span>
                  )}
                </div>
              </GlassCard>
            );
          })()}

          {/* The reasoning behind the verdict. */}
          {cell?.compliance_status && cell.ai_analysis && (
            <GlassCard icon={<PiSparkleLight />} title={t('PONS insight')} density="compact" padding="sm">
              <p className="text-xs leading-relaxed text-fg-body">{cell.ai_analysis}</p>
            </GlassCard>
          )}

          <div data-tour-id="cell-detail-answer" className="space-y-4">
          {(!cell || cell.status === 'pending') ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <GlassCard padding="lg">
                <div className="text-center">
                  {row.status === 'processing' ? (
                    <>
                      <BrandLoader size="xs" className="!h-8 !w-8 mx-auto mb-3" />
                      <p className="text-sm text-fg-muted">{t('Processing this cell…')}</p>
                    </>
                  ) : (
                    <>
                      <PiQuestionLight className="mx-auto mb-3 h-8 w-8 text-fg-muted/60" />
                      <p className="text-sm text-fg-muted">
                        {t('No answer yet. Run processing to extract this value.')}
                      </p>
                    </>
                  )}
                </div>
              </GlassCard>
            </motion.div>

          ) : cell.status === 'error' ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-danger-200 bg-danger-50 p-4 dark:border-danger-800 dark:bg-danger-900/10"
            >
              <div className="flex items-center gap-2">
                <PiWarningLight className="h-4 w-4 text-danger-500" />
                <p className="text-sm font-medium text-danger-700 dark:text-danger-400">
                  {t('Error extracting this value')}
                </p>
              </div>
            </motion.div>

          ) : (
            <>
              {/* ONLY WHEN THERE IS ONE. `cell.value || "—"` is how the empty
                  answer got drawn on every compliance cell. */}
              {!!(cell.value && String(cell.value).trim()) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: 0.05 }}
                >
                  {/* PONS OUTPUT. The answer is what the page is for, so it leads
                      the pane and carries the accent hairline that marks AI
                      output - a hairline, never a gold fill. */}
                  <GlassCard
                    icon={<PiSparkleLight className="text-champagne" />}
                    title={t('Answer')}
                    density="compact"
                    padding="sm"
                    className="border-s-2 border-s-champagne"
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-fg-strong">
                      {cell.value}
                    </p>
                  </GlassCard>
                </motion.div>
              )}

              {cell.source_excerpt && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: 0.15 }}
                >
                  <GlassCard
                    icon={<PiQuotesLight />}
                    title={t('Source excerpt')}
                    density="compact"
                    padding="sm"
                    className="border-s-4 border-l-hairline"
                  >
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-fg-muted">
                      &ldquo;{cell.source_excerpt}&rdquo;
                    </p>
                  </GlassCard>
                </motion.div>
              )}
            </>
          )}
          </div>

          {/* WHAT this is - the document and the question asked of it. Below
              the answer: it is context for the result, not the result. */}
          <SectionLabel>{t('Details')}</SectionLabel>
          <div data-tour-id="cell-detail-info" className="grid grid-cols-2 gap-3">
            <GlassCard icon={<PiFileTextLight />} title={t('Document')} density="compact" padding="sm">
              <p className="truncate text-xs font-medium text-fg-body" title={row.file_name}>
                {row.file_name}
              </p>
            </GlassCard>
            <GlassCard icon={<PiColumnsLight />} title={t('Column')} density="compact" padding="sm">
              <p className="truncate text-xs font-medium text-fg-body" title={column.name}>
                {column.name}
              </p>
            </GlassCard>
          </div>

          {column.query && (
            <GlassCard icon={<PiTargetLight />} title={t('Query')} density="compact" padding="sm">
              <p className="text-xs leading-relaxed text-fg-muted">{column.query}</p>
            </GlassCard>
          )}

          {/* ── DEEP DIVE ─────────────────────────────────────────────────── */}
          {cell?.compliance_status && (
            <>
              <SectionLabel>{t('Deep dive')}</SectionLabel>
              <GlassCard padding="sm">
                {row.review_session_uuid ? (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs leading-relaxed text-fg-muted">
                      {t('This document already has a review session.')}
                    </p>
                    <Button
                      variant="outline"
                      size="small"
                      className="flex-shrink-0"
                      icon={<PiShieldCheckLight className="h-3.5 w-3.5" />}
                      onClick={() => history.push(`/reviews/${row.review_session_uuid}?source=table&table_uuid=${tableUuid}`)}
                    >
                      {t('View in review')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs leading-relaxed text-fg-muted">
                      {t('Open this document as a full review to see every clause checked against the playbook.')}
                    </p>
                    <Button
                      variant="outline"
                      size="small"
                      className="flex-shrink-0"
                      icon={<PiShieldCheckLight className="h-3.5 w-3.5" />}
                      disabled={!row.id}
                      onClick={async () => {
                        if (!row.id) return;
                        const result = await dispatch(createDeepDive(tableUuid, row.id) as any);
                        if (result?.review_session_uuid) {
                          history.push(`/reviews/${result.review_session_uuid}?source=table&table_uuid=${tableUuid}`);
                        }
                      }}
                    >
                      {t('Start deep dive')}
                    </Button>
                  </div>
                )}
              </GlassCard>
            </>
          )}

          {/* ── COMMENTS ──────────────────────────────────────────────────
              Last, and COLLAPSIBLE. This is the least primary thing on the
              panel - the analysis is why it was opened - but with a composer
              and a list it was also the tallest, pushing the result off the
              top on any cell with a conversation on it.

              Open when there is something to read, shut when there is not: an
              empty comments block asking to be filled is not worth the height
              it takes from the finding above it. Either way the toggle says
              how many there are, so nothing is hidden without saying so. */}
          <motion.div
            data-tour-id="cell-detail-comments"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <button
              type="button"
              onClick={() => { commentsTouchedRef.current = true; setCommentsOpen((open) => !open); }}
              aria-expanded={commentsOpen}
              data-testid="cell-comments-toggle"
              className="mb-3 flex w-full items-center gap-1.5 rounded-md py-1 text-[11px] font-medium text-fg-muted transition-colors hover:text-fg-body"
            >
              <PiChatTextLight className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{t('Comments')}</span>
              {cellComments.length > 0 && (
                <span className="tabular-nums normal-case">({cellComments.length})</span>
              )}
              <PiCaretDownLight
                className={clsx(
                  'ms-auto h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ease-slide',
                  commentsOpen ? 'rotate-180' : 'rotate-0',
                )}
              />
            </button>

            {commentsOpen && (<>

            {/* Comment List */}
            <div className="space-y-3 mb-3">
              {isLoadingComments ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="glyph" className="!h-4 !w-4" />
                </div>
              ) : cellComments.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-3">
                  {t('No comments yet. Be the first to add one.')}
                </p>
              ) : (
                cellComments.map((comment: CellComment) => {
                  const isOwn = comment.user_uuid === currentUserUuid;
                  const isEditing = editingCommentId === comment.id;

                  return (
                    <div
                      key={comment.id}
                      className="group rounded-xl glass shadow-sm p-3"
                    >
                      {/* Comment header */}
                      <div className="flex items-center gap-2 mb-1.5">
                        {/* Avatar */}
                        <ProfileImage
                          profilePicture={normalise(comment).avatar}
                          email={comment.user_email || null}
                          initials={comment.user_initials || comment.user_name?.charAt(0) || '?'}
                          name={comment.user_name}
                          size="w-5 h-5"
                          className="text-[8px]"
                        />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                          {comment.user_name}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {formatCommentTime(comment.created_at)}
                        </span>
                        {comment.is_edited && (
                          <span className="text-[9px] text-gray-400 dark:text-gray-600 italic">
                            ({t('edited')})
                          </span>
                        )}

                        {/* Own comment actions */}
                        {isOwn && !isEditing && (
                          <div className="ms-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingText(comment.comment_text);
                              }}
                              className="p-0.5 rounded hover:bg-surface-soft transition-colors"
                              title={t('Edit')}
                            >
                              <PiPencilSimpleLight className="w-3 h-3 text-gray-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-0.5 rounded hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                              title={t('Delete')}
                            >
                              <PiTrashLight className="w-3 h-3 text-danger-400" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Comment body */}
                      {isEditing ? (
                        <div className="mt-1 relative">
                          <div className="relative">
                            {/* Highlight overlay for edit textarea */}
                            {editingText.includes('@[') && (
                              <div
                                aria-hidden
                                className="absolute inset-0 px-2.5 py-1.5 text-xs leading-normal whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-gray-800 dark:text-gray-200"
                                style={{ wordBreak: 'break-word' }}
                              >
                                {renderInputHighlight(editingText)}
                              </div>
                            )}
                            <textarea
                              value={editingText}
                              onChange={handleEditCommentChange}
                              onKeyDown={(e) => handleEditKeyDown(e, comment.id)}
                              rows={2}
                              className="w-full text-xs bg-surface border border-hairline rounded-md px-2.5 py-1.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-700 resize-none relative z-[1] caret-gray-800 dark:caret-gray-200"
                              style={{ color: editingText.includes('@[') ? 'transparent' : undefined }}
                              autoFocus
                            />
                          </div>
                          {showMentionDropdown && mentionTarget === 'edit' && <MentionDropdown />}
                          <div className="flex items-center gap-2 mt-1.5">
                            <Button
                              variant="primary"
                              size="small"
                              onClick={() => handleUpdateComment(comment.id)}
                              disabled={!editingText.trim()}
                            >
                              {t('Save')}
                            </Button>
                            <Button
                              variant="neutral"
                              size="small"
                              onClick={() => { setEditingCommentId(null); setEditingText(''); }}
                            >
                              {t('Cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                          {renderCommentText(comment.comment_text)}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* New Comment Input */}
            {cell && (
              <div className="relative">
                {/* Mention dropdown rendered outside the bordered box so it isn't clipped */}
                {showMentionDropdown && mentionTarget === 'new' && <MentionDropdown />}
                <div className="rounded-xl glass shadow-sm">
                  <div className="relative">
                    {/* Highlight overlay - only shown when text contains mentions */}
                    {commentText.includes('@[') && (
                      <div
                        aria-hidden
                        className="absolute inset-0 px-3 py-2.5 text-xs leading-normal whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-gray-800 dark:text-gray-200"
                        style={{ wordBreak: 'break-word' }}
                      >
                        {renderInputHighlight(commentText)}
                      </div>
                    )}
                    <textarea
                      ref={commentInputRef}
                      value={commentText}
                      onChange={handleCommentChange}
                      onKeyDown={handleKeyDown}
                      placeholder={t('Write a comment… Use @ to mention someone')}
                      rows={2}
                      className="w-full text-xs bg-transparent px-3 py-2.5 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none resize-none rounded-t-lg relative z-[1] caret-gray-800 dark:caret-gray-200"
                      style={{ color: commentText.includes('@[') ? 'transparent' : undefined }}
                    />
                  </div>
                  <div className="flex items-center justify-between px-2 py-1.5 border-t border-hairline-soft dark:border-white/[0.06]">
                    <button
                      type="button"
                      onClick={handleAtButtonClick}
                      className="p-1.5 rounded-md hover:bg-surface-soft transition-colors text-gray-400 hover:text-info-500 dark:hover:text-info-400"
                      title={t('Mention someone')}
                    >
                      <PiAtLight className="w-4 h-4" />
                    </button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || isSubmitting}
                      isLoading={isSubmitting}
                      icon={<PiPaperPlaneRightLight className="w-3.5 h-3.5" />}
                    >
                      {t('Send')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            </>)}
          </motion.div>
        </div>
    </>
  );

  const tour = (
    <FeatureTour
      steps={cellDetailTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  );

  // Docked: in the flow of its pane, which supplies the glass and the slide.
  if (docked) {
    return (
      <div className="flex flex-col h-full min-h-0" data-testid="cell-detail-docked">
        {body}
        {tour}
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed inset-y-0 end-0 z-50 w-full max-w-lg glass !border-y-0 !border-r-0 shadow-xl flex flex-col"
      >
        {body}
      </motion.div>
      {tour}
    </AnimatePresence>
  );
};

export default CellDetailPanel;